/**
 * Cloud recording API client.
 *
 * Cloud recording is composited and written by the mixer MCU on the server, so
 * everything here is a control call — the browser never touches media. That is
 * the whole difference from local recording, which composites in a canvas on
 * this machine and writes to the user's Downloads folder.
 *
 * Playback and thumbnail URLs are permanent public CDN links — safe to cache,
 * reuse and hand to an <img> or <video> without refetching. They are also
 * unauthenticated, so treat them as shareable: anyone given one can watch the
 * recording without signing in.
 */

import { config } from '@/config/env'

/**
 * Make a recording URL fetchable from this page.
 *
 * S3-hosted recordings come back as absolute CDN links and are returned
 * untouched. A recording stored by the BACKEND comes back as a path —
 * `/api/v1/recordings/<id>/file?t=…` — because the backend is not told its own
 * public address anywhere in its config.
 *
 * That path has to resolve against the API, not against the page. In
 * development the two share an origin, since Vite proxies `/api` to the
 * backend, so a bare path works by accident and hides the problem. In
 * production they are different hosts — the dashboard on one, the API on
 * `VITE_API_URL` — so the browser would resolve against the dashboard and 404
 * on every play, download and thumbnail.
 *
 * Same helper and same reasoning as `absolutize` in chat-attachment.api.
 */
export function absolutizeRecordingUrl(
  url: string | null,
  /**
   * The API origin. Defaulted rather than read inside, because `config.apiUrl`
   * is EMPTY in dev and in tests — same-origin through the Vite proxy — so a
   * test that let it default would compare '' + url against url and pass
   * whatever the function did. Mutation testing is how that was caught: every
   * rule here survived being deleted.
   */
  apiBase: string = config.apiUrl ?? '',
): string | null {
  if (!url) return null
  if (/^https?:\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url
  const base = apiBase.replace(/\/+$/, '')
  // Empty means same origin, which is dev. The path is already correct there.
  if (!base) return url
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`
}

const API = config.apiUrl

export type RecordingStatus = 'recording' | 'uploading' | 'ready' | 'failed'

export interface Recording {
  id: string
  meetingId: string | null
  roomId: string
  status: RecordingStatus
  durationSeconds: number | null
  bytes: number | null
  mimeType: string
  startedAt: string | null
  endedAt: string | null
  createdAt: string
  /**
   * CDN URL to play or download it. Null unless status is 'ready'. A private
   * recording's is signed and lapses at `playbackUrlExpiresAt`; open it through
   * recording-link.ts, which asks again when it would lapse mid-viewing.
   */
  playbackUrl: string | null
  /** When `playbackUrl` stops opening (ISO); null for one that does not. */
  playbackUrlExpiresAt?: string | null
  /**
   * CDN URL for the still frame (signed and short-lived for a private
   * recording, which is fine: an <img> loads it at once). Null when the mixer produced
   * none — generation is best-effort, so callers must handle null rather than
   * assume every ready recording has one.
   */
  thumbnailUrl: string | null
}

export class RecordingError extends Error {}

function appToken(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') || sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const session = (JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } })
      ?.session
    if (session?.expiresAt && session.expiresAt <= Date.now()) return ''
    return session?.accessToken || ''
  } catch {
    return ''
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = appToken()
  if (!token) throw new RecordingError('Not signed in')

  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  })

  if (!res.ok) {
    // Surface the server's own reason. "Only the meeting host can start a
    // recording" is actionable; "Request failed" sends someone to the console.
    let message = `Request failed (${res.status})`
    try {
      const body = await res.json()
      message = body?.error?.message || message
    } catch {
      /* keep the status-based fallback */
    }
    throw new RecordingError(message)
  }

  return (await res.json()) as T
}

/** Start recording a meeting. Idempotent — returns the existing one if active. */
export function startRecording(meetingId: string, layout = 'grid') {
  return request<Recording & { alreadyActive?: boolean }>(
    `/api/v1/meetings/${meetingId}/recording/start`,
    { method: 'POST', body: JSON.stringify({ layout }) },
  )
}

/**
 * Stop recording. Resolves once the mixer has been told to stop, not once the
 * file is available: the upload continues server-side and the recording moves
 * to 'ready' when the mixer reports it.
 */
export function stopRecording(meetingId: string) {
  return request<{ ok: boolean; status: string; roomId: string }>(
    `/api/v1/meetings/${meetingId}/recording/stop`,
    { method: 'POST' },
  )
}

/** Whether a recording is currently running for this meeting. */
export function getRecordingStatus(meetingId: string) {
  return request<{ active: boolean } & Partial<Recording>>(
    `/api/v1/meetings/${meetingId}/recording`,
  )
}

/** Recordings the caller can watch, newest first. */
export function listRecordings(opts: { meetingId?: string; limit?: number; offset?: number } = {}) {
  const qs = new URLSearchParams()
  if (opts.meetingId) qs.set('meetingId', opts.meetingId)
  if (opts.limit != null) qs.set('limit', String(opts.limit))
  if (opts.offset != null) qs.set('offset', String(opts.offset))
  const suffix = qs.toString() ? `?${qs}` : ''
  return request<{ recordings: Recording[]; limit: number; offset: number }>(
    `/api/v1/recordings${suffix}`,
  )
}

/** One recording, with a freshly minted playback URL. */
export function getRecording(id: string) {
  return request<Recording>(`/api/v1/recordings/${id}`)
}

export function deleteRecording(id: string) {
  return request<{ ok: boolean }>(`/api/v1/recordings/${id}`, { method: 'DELETE' })
}
